/**
 * Store-shaped formatting helpers.
 *
 * Lebron and Atlas both hand back times as bare `HH:MM:SS` strings in the
 * store's own timezone, and the OOS batch interval as an ISO-8601 duration.
 * Nothing here assumes the browser's locale or timezone matches the store's.
 */

/** `"07:00:00"` → `"07:00"`. Leaves anything unparseable untouched. */
export function formatTimeOfDay(time: string | null | undefined): string {
  if (!time) return "—";
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return time;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

/** `"07:00:00"` + `"22:30:00"` → `"07:00 – 22:30"`. */
export function formatHourRange(
  opening: string | null | undefined,
  closing: string | null | undefined,
): string {
  if (!opening && !closing) return "—";
  return `${formatTimeOfDay(opening)} – ${formatTimeOfDay(closing)}`;
}

/** Minutes a store is open, handling ranges that wrap past midnight. */
export function openMinutes(
  opening: string | null | undefined,
  closing: string | null | undefined,
): number | null {
  const start = timeToMinutes(opening);
  const end = timeToMinutes(closing);
  if (start === null || end === null) return null;
  const span = end - start;
  return span >= 0 ? span : span + 24 * 60;
}

function timeToMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * ISO-8601 duration (`"PT2H30M"`) or a `HH:MM:SS` string → `"2h 30m"`.
 *
 * The OpenAPI spec types `oos_event_batch_interval` as `format: duration`,
 * which FastAPI serialises as ISO-8601, but older rows can still come back in
 * the Django `HH:MM:SS` shape — both are handled.
 */
export function formatDuration(value: string | null | undefined): string {
  if (!value) return "—";

  const iso =
    /^P(?:(\d+)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(
      value,
    );
  if (iso) {
    const [, days, hours, minutes, seconds] = iso;
    const parts = [
      days && `${Number(days)}d`,
      hours && `${Number(hours)}h`,
      minutes && `${Number(minutes)}m`,
      seconds && Number(seconds) > 0 && `${Number(seconds)}s`,
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : "0m";
  }

  const clock = /^(\d+):(\d{2}):(\d{2})/.exec(value);
  if (clock) {
    const [, hours, minutes, seconds] = clock;
    const parts = [
      Number(hours) > 0 && `${Number(hours)}h`,
      Number(minutes) > 0 && `${Number(minutes)}m`,
      Number(seconds) > 0 && `${Number(seconds)}s`,
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : "0m";
  }

  return value;
}

/** Duration in whole minutes, for deriving run counts. Null if unparseable. */
export function durationMinutes(
  value: string | null | undefined,
): number | null {
  if (!value) return null;

  const iso =
    /^P(?:(\d+)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(
      value,
    );
  if (iso) {
    const [, days, hours, minutes, seconds] = iso;
    const total =
      Number(days ?? 0) * 1440 +
      Number(hours ?? 0) * 60 +
      Number(minutes ?? 0) +
      Number(seconds ?? 0) / 60;
    return total > 0 ? total : null;
  }

  const clock = /^(\d+):(\d{2}):(\d{2})/.exec(value);
  if (clock) {
    const [, hours, minutes, seconds] = clock;
    const total = Number(hours) * 60 + Number(minutes) + Number(seconds) / 60;
    return total > 0 ? total : null;
  }

  return null;
}

/** Current wall-clock time in a store's timezone, as `HH:MM`. */
export function localTimeIn(timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date());
  } catch {
    // An unknown IANA zone would otherwise throw on every render.
    return null;
  }
}

/** Whether a store is inside its opening hours right now, in its own timezone. */
export function isOpenNow(
  timezone: string,
  opening: string | null | undefined,
  closing: string | null | undefined,
): boolean | null {
  const now = localTimeIn(timezone);
  if (!now) return null;
  const nowMinutes = timeToMinutes(now);
  const start = timeToMinutes(opening);
  const end = timeToMinutes(closing);
  if (nowMinutes === null || start === null || end === null) return null;
  // A closing time before the opening time means the store runs past midnight.
  return start <= end
    ? nowMinutes >= start && nowMinutes < end
    : nowMinutes >= start || nowMinutes < end;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export const fmt = {
  timeOfDay: formatTimeOfDay,
  hourRange: formatHourRange,
  duration: formatDuration,
  dateTime: formatDateTime,
};
